/**
 * AdminServiziPage — F-009.6.
 *
 * Schermata ADMIN per gestire in autonomia il catalogo dei servizi extra:
 * aggiunta, modifica (nome/prezzo/attivo) ed eliminazione. I servizi attivi
 * sono acquistabili dagli agenti dal proprio wallet.
 *
 * Struttura (pattern admin-wallet): guard ADMIN + AppLayout + QueryClientProvider
 * esterno; `AdminServiziPageInner` interno (esportato per i test).
 */
import { AlertCircle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { serviziQueryClient } from '../lib/query-client';
import { errorMessageFor } from '../lib/servizi-client';
import {
  useServizi,
  useCreateServizio,
  useUpdateServizio,
  useDeleteServizio,
} from '../hooks/useServizi';
import { AddServizioForm } from '../components/AddServizioForm';
import { ServizioRow } from '../components/ServizioRow';

export function AdminServiziPage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={serviziQueryClient}>
          <AdminServiziPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminServiziPageInner() {
  const query = useServizi();
  const create = useCreateServizio();
  const update = useUpdateServizio();
  const del = useDeleteServizio();

  const items = query.data?.items ?? [];

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Catalogo servizi</h1>
        <p className="text-muted-foreground">
          Servizi extra oltre alle registrazioni (es. consulenze, pacchetti). I servizi
          attivi sono acquistabili dagli agenti dal proprio wallet.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Aggiungi servizio</CardTitle>
        </CardHeader>
        <CardContent>
          <AddServizioForm
            onCreate={(body) => create.mutate(body)}
            isPending={create.isPending}
          />
        </CardContent>
      </Card>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(query.error)}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento servizi…
        </p>
      ) : null}

      {!query.isLoading && !query.isError ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Servizi ({items.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                Nessun servizio. Aggiungine uno qui sopra.
              </div>
            ) : (
              <table className="w-full border-collapse text-sm" aria-label="Catalogo servizi">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Nome
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Prezzo
                    </th>
                    <th scope="col" className="py-2 pr-4 font-medium">
                      Stato
                    </th>
                    <th scope="col" className="py-2 text-right font-medium">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((servizio) => (
                    <ServizioRow
                      key={servizio.id}
                      servizio={servizio}
                      onSave={(body) => update.mutate({ id: servizio.id, body })}
                      onDelete={() => del.mutate(servizio.id)}
                      isSaving={update.isPending && update.variables?.id === servizio.id}
                      isDeleting={del.isPending && del.variables === servizio.id}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}
